import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isNetworkAvailable, retryFailedOperations, startAutomaticSync, syncPendingOperations, syncSummary } from "@/lib/offlineSync";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useTranslation } from "react-i18next";

export default function EmployeeSyncCenter() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [online, setOnline] = useState(() => navigator.onLine);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [summary, setSummary] = useState({ pending: 0, pendingEvidence: 0, failed: 0, reviewRequired: 0, lastSuccessfulSync: null });

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    try { setSummary(await syncSummary(user.id)); } catch { /* browser remains online-only */ }
  }, [user?.id]);

  const run = useCallback(async () => {
    if (!user?.id || !isNetworkAvailable() || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await syncPendingOperations({
        userId: user.id,
        postOperation: (operation) => api.post("/offline-sync/operations", operation, { timeout: 10000 }),
        uploadEvidence: async (file, metadata) => {
          const body = new FormData();
          body.append("file", file);
          body.append("source_operation_id", metadata.operationId);
          body.append("evidence_id", metadata.evidenceId);
          body.append("captured_at", metadata.capturedAt);
          const response = await api.post("/upload", body, { timeout: 15000 });
          return response.data || {};
        },
      });
      await refresh();
      window.dispatchEvent(new CustomEvent("ajfarm:sync-updated"));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [refresh, user?.id]);

  useEffect(() => {
    const connection = () => { setOnline(navigator.onLine); refresh(); };
    const changed = () => { refresh(); if (isNetworkAvailable()) run(); };
    window.addEventListener("online", connection);
    window.addEventListener("offline", connection);
    window.addEventListener("ajfarm:sync-updated", changed);
    refresh();
    const stop = startAutomaticSync(run);
    return () => {
      stop();
      window.removeEventListener("online", connection);
      window.removeEventListener("offline", connection);
      window.removeEventListener("ajfarm:sync-updated", changed);
    };
  }, [refresh, run]);

  const manualRetry = async () => {
    await retryFailedOperations(user.id);
    await run();
  };
  const status = busy
    ? t("offline.status.syncing")
    : summary.reviewRequired ? t("offline.status.review")
      : summary.failed ? t("offline.status.failed")
      : online
      ? (summary.pending ? t("offline.status.pending") : t("offline.status.online"))
      : t("offline.status.offline");

  return <Card className="mb-4 p-3 border-primary/20" data-testid="employee-sync-center-global">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-semibold">{status}</div>
        <div className="text-xs text-muted-foreground break-words">
          {t("offline.summary", { pending: summary.pending, failed: summary.failed, review: summary.reviewRequired })}
          {` · ${t("offline.photos_pending", { count: summary.pendingEvidence })}`}
          {summary.lastSuccessfulSync ? ` · ${t("offline.last_synced", { time: new Date(summary.lastSuccessfulSync).toLocaleString() })}` : ""}
        </div>
      </div>
      <Button type="button" variant="outline" onClick={manualRetry} disabled={!online || busy || (!summary.pending && !summary.failed)} className="h-11" data-testid="employee-sync-retry">
        {busy ? t("offline.status.syncing") : t("offline.retry")}
      </Button>
    </div>
  </Card>;
}
